import api from './api';
import type { ApiResponse } from '@/types/api.types';
import type { RestoreRequestKind, RestoreRequestStatus } from '@/types/enums.generated';

/**
 * ADR-044 restore control plane v2.
 *
 * The console never talks to the agent. It queues a restore request on the
 * backend (POST /watchers/:id/restore-requests); the agent collects it on its
 * next poll (≤ 60s), restores, and reports back; the console polls the request
 * for its status. This replaced watcher.service.ts, which called the agent's
 * LAN address (http://192.168.1.114:8081) straight from the browser.
 */
export interface RestoreRequest {
  id: string;
  watcher_id: string;
  watcher_name: string;
  kind: RestoreRequestKind;
  /** empty for kind=all */
  file_ids: string[];
  file_count: number;
  restore_path: string;
  status: RestoreRequestStatus;
  files_restored: number;
  files_failed: number;
  error?: string | null;
  requested_by: string;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface CreateRestoreRequest {
  kind: RestoreRequestKind;
  /** required for kind=files, must be empty for kind=all */
  file_ids?: string[];
  restore_path: string;
}

/** Error codes the backend answers a create with (400 / 409). */
export const RESTORE_REFUSAL_MESSAGES: Record<string, string> = {
  WATCHER_OFFLINE: '此 Watcher 離線（5 分鐘內無心跳），無法接收還原指令。請確認 agent 正在執行。',
  WATCHER_DISABLED: '此 Watcher 已停用。請至「File Watcher 管理」頁啟用後再試。',
  WATCHER_AGENT_OUTDATED:
    '此 Watcher 的 agent 版本過舊，不支援遠端還原。請重新下載安裝包更新 agent。',
  RESTORE_FILES_REQUIRED: '請選擇至少一個檔案。',
  RESTORE_FILES_INVALID: '所選檔案不是由此 Watcher 備份的，無法由它還原。',
};

export function isRestoreTerminal(status: RestoreRequestStatus): boolean {
  return status === 'done' || status === 'failed';
}

export interface WaitOptions {
  intervalMs?: number;
  timeoutMs?: number;
  onUpdate?: (request: RestoreRequest) => void;
  signal?: AbortSignal;
}

export const restoreService = {
  /**
   * Queue a restore for a watcher. 202 with the pending request; the page
   * handles 400 / 409 refusals itself (skipGlobalError), see
   * RESTORE_REFUSAL_MESSAGES.
   */
  async createRestoreRequest(watcherId: string, body: CreateRestoreRequest): Promise<RestoreRequest> {
    const response = await api.post<ApiResponse<RestoreRequest>>(
      `/api/v1/watchers/${watcherId}/restore-requests`,
      body,
      { skipGlobalError: true },
    );
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to create restore request');
  },

  async getRestoreRequest(watcherId: string, requestId: string): Promise<RestoreRequest> {
    const response = await api.get<ApiResponse<RestoreRequest>>(
      `/api/v1/watchers/${watcherId}/restore-requests/${requestId}`,
    );
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to read restore request');
  },

  /**
   * Restore history (GET /backup/restore-history): the 100 most recent
   * requests the caller may see, newest first.
   */
  async getRestoreHistory(): Promise<RestoreRequest[]> {
    const response = await api.get<ApiResponse<{ items: RestoreRequest[]; total: number }>>(
      '/api/v1/backup/restore-history',
    );
    return response.data.data?.items ?? [];
  },

  /**
   * Poll a request until it is done or failed. Resolves with the final row;
   * rejects on timeout or abort. The agent polls every 60s, so the first
   * transition can take a minute.
   */
  async waitForRestore(watcherId: string, requestId: string, opts: WaitOptions = {}): Promise<RestoreRequest> {
    const intervalMs = opts.intervalMs ?? 3000;
    const timeoutMs = opts.timeoutMs ?? 10 * 60 * 1000;
    const deadline = Date.now() + timeoutMs;
    for (;;) {
      if (opts.signal?.aborted) throw new Error('aborted');
      const current = await this.getRestoreRequest(watcherId, requestId);
      opts.onUpdate?.(current);
      if (isRestoreTerminal(current.status)) return current;
      if (Date.now() >= deadline) throw new Error('restore request timed out');
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  },
};
